import React from "react";
import { currencyFormatter } from "@sia/style-guide";

function extractActiveFilters(filters, filterId, isInitialLoad) {
  const DEFAULT_ITEMS = [];
  if (isInitialLoad) return DEFAULT_ITEMS;
  let filter = filters.filters.find((e) => e.id === filterId);
  if (!filter) return DEFAULT_ITEMS;
  if (filter.type === "dropDown") {
    return filter.value?.id ? [filter.value.id] : DEFAULT_ITEMS;
  }
  return filter.items.filter((e) => e.active).map((e) => e.id);
}

export function extractRequestBodyFromFilters(filters, isInitialLoad) {
  const selectedYears = extractActiveFilters(filters, "surveyYears", isInitialLoad);
  const selectedYear = selectedYears?.[0] ?? null;
  const dataBasisIds = extractActiveFilters(filters, "dataBasis", isInitialLoad);
  const selectedDataBasis = dataBasisIds?.[0] ?? null;
  const levelId = extractActiveFilters(filters, "levelId", isInitialLoad);
  const functionId = extractActiveFilters(filters, "functionId", isInitialLoad);

  return {
    surveyYear: selectedYear ?? 2025,
    dataBasis: selectedDataBasis ?? "company",
    levelId,
    functionId,
  };
}

function setInitialActiveSurveyYear(filtersCopy) {
  const surveyYearsFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "surveyYears");
  if (surveyYearsFilterIndex === -1) return filtersCopy;
  const firstItem = filtersCopy.filters[surveyYearsFilterIndex].items[0] ?? null;
  filtersCopy.filters[surveyYearsFilterIndex].value = firstItem;
  return filtersCopy;
}

function setInitialActiveDataBasis(filtersCopy) {
  const dataBasisFilterIndex = filtersCopy.filters.findIndex((e) => e.id === "dataBasis");
  if (dataBasisFilterIndex === -1) return filtersCopy;
  const firstItem = filtersCopy.filters[dataBasisFilterIndex].items[0] ?? null;
  filtersCopy.filters[dataBasisFilterIndex].value = firstItem;
  return filtersCopy;
}

const getOptionId = (item) => {
  if (item && typeof item === "object") return item.id;
  return item;
};

const getOptionLabel = (item) => {
  if (item && typeof item === "object") {
    return item.value ?? item.label ?? item.name ?? item.id;
  }
  return item;
};

const getSubmittedIds = (submitted) => {
  if (!submitted) return [];

  const asArray = Array.isArray(submitted) ? submitted : [submitted];

  return asArray.map((item) => {
    if (item && typeof item === "object") return item.id;
    return item;
  });
};

export async function updateFilters(filters, submitedFilters, nextAvailableFilters, isInitialLoad, isMasterKeyValid, translate) {
  let filtersCopy = { ...filters };
  const databasisFilter = {
    dataBasis: [
      // { id: "benchmark", value: translate("benchmarking.calculations-page.filters.dataBasis.benchmark") },
      { id: "company", value: translate("benchmarking.calculations-page.filters.dataBasis.company") },
    ],
  };
  if (isInitialLoad) {
    filtersCopy.allAvailableFilters = {
      ...nextAvailableFilters,
      ...databasisFilter,
    };
  }
  filtersCopy.filters = filtersCopy.filters.map((filter) => {
    let filterCopy = { ...filter };
    const available = filtersCopy.allAvailableFilters[filter.id] || [];
    const filtered = filter.id === "dataBasis" && !isMasterKeyValid ? available.filter((x) => x.id !== "company") : available;
    if (filter.id === "surveyYears") {
      filterCopy.type = "dropDown";
      filterCopy.items = filtered.map((item) => ({
        id: item,
        label: item,
        name: item,
      }));
      const selectedId = submitedFilters.surveyYear;
      const bySubmitted = filterCopy.items.find((it) => it.id === selectedId);
      filterCopy.value = bySubmitted ?? filterCopy.items[0] ?? null;
      return filterCopy;
    }
    if (filter.id === "dataBasis") {
      filterCopy.type = "dropDown";
      filterCopy.items = filtered.map((item) => ({
        id: item.id,
        label: item.value,
        name: item.value,
      }));
      const selectedId = submitedFilters.dataBasis;
      const bySubmitted = filterCopy.items.find((it) => it.id === selectedId);
      filterCopy.value = bySubmitted ?? filterCopy.items[0] ?? null;
      return filterCopy;
    }
    const submittedIds = getSubmittedIds(submitedFilters[filter.id]);
    filterCopy.type = "dropDown";
    filterCopy.items = filtered.map((item) => {
      const id = getOptionId(item);
      const label = getOptionLabel(item);

      return {
        id,
        label: String(label),
        name: String(label),
        active: submittedIds.includes(id),
      };
    });
    const selected = filterCopy.items.find((item) => item.active);
    filterCopy.value = selected ?? filterCopy.items[0] ?? null;

    return filterCopy;
  });
  if (isInitialLoad) {
    filtersCopy = setInitialActiveSurveyYear(filtersCopy);
    filtersCopy = setInitialActiveDataBasis(filtersCopy);
    return { ...filtersCopy, newest: true, isInitialLoad: false };
  }

  return { ...filtersCopy, newest: false, isInitialLoad: false };
}

// ---------------------------
// Table functions
// ---------------------------
function isNotDelivered(value, containerObj) {
  // Covers the common "missing" cases + optional flags if the API provides them.
  const missing = value === null || value === undefined;

  const flaggedNotDelivered = containerObj?.delivered === false || containerObj?.isDelivered === false || containerObj?.notDelivered === true;

  // If the API encodes "not delivered" as 0, show empty when flagged.
  const zeroButFlagged = value === 0 && flaggedNotDelivered;

  return missing || zeroButFlagged;
}

function formatAlternateMaybe(value, containerObj) {
  if (isNotDelivered(value, containerObj)) return "";
  return currencyFormatter.formatAlternate(value);
}

function formatDeltaMaybe(value, containerObj) {
  if (isNotDelivered(value, containerObj)) return "";
  const num = Number(value);
  if (Number.isNaN(num)) return "";
  return num.toFixed(1);
}

// eslint-disable-next-line react/prop-types
function ProfitInput({ value, onChange }) {
  const safeValue = value ?? "";
  return (
    <input
      type="number"
      step="0.1"
      value={safeValue}
      onChange={(e) => {
        const raw = e.target.value;
        const next = raw === "" ? null : Number(raw);
        onChange(next.toFixed(1));
      }}
      className="table-input right-align"
    />
  );
}

export function parseTableData(data, translate, profitMarginInputs, onProfitMarginChange) {
  if (!data || data.length === 0) return [];

  // helper: formats percentage or blank
  const pct = (v, obj) => formatDeltaMaybe(v, obj);

  // helper: format CHF-like numbers (keep your existing formatAlternateMaybe)
  const chf = (v, obj) => formatAlternateMaybe(v, obj);

  const groups = [
    ["grossSalary", "totalOverheads"],
    ["selfCosts", "avgOfficeCostRate", "profitMargin"],
    ["calcFee", "avgCalcHourlyRate"],
  ];

  const renderLines = (lines, { boldLast = false } = {}) => (
    <div className="calc-multiline">
      {lines.map((node, i) => (
        <div key={i} className={[boldLast && i === lines.length - 1 ? "calc-line--bold" : ""].filter(Boolean).join(" ")}>
          {node}
        </div>
      ))}
    </div>
  );

  const buildGroupRow = (keys, { boldLastLabelAndChf = false } = {}) => {
    const labelLines = [];
    const chfLines = [];
    const pctGrossLines = [];
    const pctSelfLines = [];

    keys.forEach((k) => {
      const v = data.values[k];
      labelLines.push(translate(`benchmarking.calculations-page.table.${k}`));

      // CHF column
      chfLines.push(chf(v?.average, v));
      if (k === "profitMargin") {
        pctGrossLines.push(
          <ProfitInput value={profitMarginInputs?.pctOfGrossSalary} onChange={(val) => onProfitMarginChange("pctOfGrossSalary", val)} />
        );
      } else {
        pctGrossLines.push(pct(v?.pctOfGrossSalary, v));
      }
      if (k === "profitMargin") {
        pctSelfLines.push(<ProfitInput value={profitMarginInputs?.pctOfSelfCosts} onChange={(val) => onProfitMarginChange("pctOfSelfCosts", val)} />);
      } else {
        pctSelfLines.push(pct(v?.pctOfSelfCosts, v));
      }
    });

    return {
      className: ["calc-group-row"].filter(Boolean).join(" "),
      onRowClick: () => null,
      cells: [
        {
          key: "label",
          className: "left-align",
          value: renderLines(labelLines, { boldLast: boldLastLabelAndChf }),
        },
        ...[
          {
            key: "chf",
            className: "right-align",
            value: renderLines(chfLines, { boldLast: boldLastLabelAndChf }),
          },
          {
            key: "pctOfGrossSalary",
            className: "right-align",
            value: renderLines(pctGrossLines),
          },
          {
            key: "pctOfSelfCosts",
            className: "right-align",
            value: renderLines(pctSelfLines),
          },
        ],
      ],
    };
  };

  // last group should have last line bold (avgCalcHourlyRate)
  const rows = [
    buildGroupRow(groups[0], { boldLastLabelAndChf: false }),
    buildGroupRow(groups[1], { boldLastLabelAndChf: false }),
    buildGroupRow(groups[2], { boldLastLabelAndChf: true }),
  ];

  return rows;
}
